import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  TextField,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
  InputAdornment,
  Modal,
  Fade,
} from "@mui/material";
import { styled, keyframes } from "@mui/material/styles";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import logo from "../assets/mainlogo.png";
import { isSEB } from "../utils/isSEB";
import { LOCAL_API,API_URL } from "../config";

// ── keyframes ──
const riseIn = keyframes`
  0%  { opacity:0; transform:translateY(34px) scale(.96); }
  100%{ opacity:1; transform:translateY(0) scale(1); }
`;
const orbDrift1 = keyframes`
  0%,100%{ transform:translate(0,0) scale(1); }
  33%    { transform:translate(28px,-22px) scale(1.06); }
  66%    { transform:translate(-18px,16px) scale(.95); }
`;
const orbDrift2 = keyframes`
  0%,100%{ transform:translate(0,0) scale(1); }
  40%    { transform:translate(-24px,18px) scale(1.04); }
  75%    { transform:translate(20px,-12px) scale(.97); }
`;
const scanV = keyframes`
  0%  { transform:translateY(-100%); opacity:0; }
  8%  { opacity:1; }
  92% { opacity:1; }
  100%{ transform:translateY(1400%); opacity:0; }
`;
const shimmerBtn = keyframes`
  0%  { background-position:-200% center; }
  100%{ background-position:200% center; }
`;
const ringExpand = keyframes`
  0%  { transform:scale(.65); opacity:.4; }
  100%{ transform:scale(2.2); opacity:0; }
`;
const dotBeat = keyframes`
  0%,100%{ transform:scale(1); opacity:.28; }
  50%    { transform:scale(1.8); opacity:.9; }
`;
const lineGrow = keyframes`
  from{ scaleX:0; opacity:0; transform-origin:left; }
  to  { scaleX:1; opacity:1; transform-origin:left; }
`;
const tagSlide = keyframes`
  from{ opacity:0; transform:translateX(-14px); }
  to  { opacity:1; transform:translateX(0); }
`;
const pulse = keyframes`
  0%,100%{ box-shadow:0 0 0 0 rgba(74,222,128,.5); }
  50%    { box-shadow:0 0 0 8px rgba(74,222,128,0); }
`;

// ── styled atoms ──
const Root = styled(Box)({
  minHeight: "100vh",
  display: "grid",
  gridTemplateColumns: "1.18fr .82fr",
  fontFamily: "'Sora',sans-serif",
  overflow: "hidden",
  "@media(max-width:960px)": { gridTemplateColumns: "1fr" },
});

/* ── LEFT ── */
const Left = styled(Box)({
  position: "relative",
  background: "#060c18",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  padding: "60px 68px",
  overflow: "hidden",
  "@media(max-width:960px)": { display: "none" },
});

const MeshBg = styled(Box)({
  position: "absolute",
  inset: 0,
  pointerEvents: "none",
  background:
    "radial-gradient(ellipse 80% 60% at 18% 22%,rgba(29,78,216,.24) 0%,transparent 58%), radial-gradient(ellipse 55% 75% at 82% 78%,rgba(16,185,129,.13) 0%,transparent 55%), radial-gradient(ellipse 45% 45% at 58% 8%,rgba(139,92,246,.11) 0%,transparent 50%)",
});

const GridPat = styled(Box)({
  position: "absolute",
  inset: 0,
  pointerEvents: "none",
  backgroundImage:
    "linear-gradient(rgba(148,163,184,.038) 1px,transparent 1px),linear-gradient(90deg,rgba(148,163,184,.038) 1px,transparent 1px)",
  backgroundSize: "60px 60px",
});

const ScanBeam = styled(Box)({
  position: "absolute",
  left: 0,
  right: 0,
  height: 2,
  pointerEvents: "none",
  background:
    "linear-gradient(90deg,transparent 0%,rgba(96,165,250,.1) 35%,rgba(96,165,250,.22) 50%,rgba(96,165,250,.1) 65%,transparent 100%)",
  animation: `${scanV} 9s ease-in-out infinite`,
});

const GlowBlob = styled(Box)(({ l, t, size, color, anim, delay }) => ({
  position: "absolute",
  left: l,
  top: t,
  width: size,
  height: size,
  borderRadius: "50%",
  background: color,
  filter: "blur(92px)",
  opacity: 0.48,
  animation: `${anim} ${10 + (delay || 0)}s ease-in-out infinite`,
  animationDelay: `${delay || 0}s`,
  pointerEvents: "none",
}));

const Ring = styled(Box)(({ delay }) => ({
  position: "absolute",
  top: "36%",
  left: "20%",
  width: 300,
  height: 300,
  borderRadius: "50%",
  border: "1px solid rgba(96,165,250,.07)",
  animation: `${ringExpand} 5.5s ease-out infinite`,
  animationDelay: delay || "0s",
  pointerEvents: "none",
}));

const StarDot = styled(Box)(({ x, y, s, delay }) => ({
  position: "absolute",
  left: x,
  top: y,
  width: s || 3,
  height: s || 3,
  borderRadius: "50%",
  background: "rgba(148,163,184,.55)",
  boxShadow: `0 0 ${(s || 3) * 2}px rgba(148,163,184,.38)`,
  animation: `${dotBeat} ${3.2 + (delay || 0)}s ease-in-out infinite`,
  animationDelay: `${delay || 0}s`,
  pointerEvents: "none",
}));

const LiveBadge = styled(Box)({
  display: "inline-flex",
  alignItems: "center",
  gap: 8,
  background: "rgba(255,255,255,.04)",
  border: "1px solid rgba(255,255,255,.08)",
  borderRadius: 100,
  padding: "6px 16px",
  backdropFilter: "blur(12px)",
  marginBottom: 30,
  animation: `${tagSlide} .8s cubic-bezier(.22,1,.36,1) .25s both`,
});

const FeatureRow = styled(Box)(({ delay }) => ({
  display: "flex",
  alignItems: "center",
  gap: 14,
  marginBottom: 13,
  padding: "12px 15px",
  borderRadius: 12,
  background: "rgba(255,255,255,.028)",
  border: "1px solid rgba(255,255,255,.055)",
  transition: "all .28s ease",
  cursor: "default",
  "&:hover": {
    background: "rgba(255,255,255,.058)",
    borderColor: "rgba(96,165,250,.2)",
    transform: "translateX(6px)",
  },
  animation: `${riseIn} .6s cubic-bezier(.22,1,.36,1) both`,
  animationDelay: delay || "0s",
}));

/* ── RIGHT ── */
const Right = styled(Box)({
  background: "#eef2f9",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  padding: "44px 44px",
  position: "relative",
  overflow: "hidden",
});

const NoiseTex = styled(Box)({
  position: "absolute",
  inset: 0,
  pointerEvents: "none",
  backgroundImage:
    "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 512 512' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='.85' numOctaves='4'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='.02'/%3E%3C/svg%3E\")",
});

const CornerBlob = styled(Box)(({ pos }) => ({
  position: "absolute",
  borderRadius: "50%",
  pointerEvents: "none",
  ...(pos === "tr" && {
    top: -100,
    right: -100,
    width: 320,
    height: 320,
    background: "radial-gradient(circle,rgba(59,130,246,.08),transparent)",
  }),
  ...(pos === "bl" && {
    bottom: -80,
    left: -80,
    width: 240,
    height: 240,
    background: "radial-gradient(circle,rgba(16,185,129,.06),transparent)",
  }),
}));

const GlassCard = styled(Box)({
  width: "100%",
  maxWidth: 405,
  position: "relative",
  zIndex: 1,
  animation: `${riseIn} .72s cubic-bezier(.22,1,.36,1) .12s both`,
});

const LogoWrap = styled(Box)({
  position: "relative",
  width: 60,
  height: 60,
  marginBottom: 26,
  "&::before": {
    content: '""',
    position: "absolute",
    inset: -5,
    borderRadius: 21,
    border: "1.5px solid rgba(59,130,246,.22)",
  },
  "&::after": {
    content: '""',
    position: "absolute",
    inset: -12,
    borderRadius: 26,
    border: "1px solid rgba(59,130,246,.1)",
  },
});

const LogoBox = styled(Box)({
  width: 60,
  height: 60,
  borderRadius: 18,
  background: "linear-gradient(145deg,#162959,#1d4ed8,#2563eb)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  boxShadow: "0 18px 44px -10px rgba(37,99,235,.52)",
});

const StyledInput = styled(TextField)({
  "& .MuiInputBase-root": {
    borderRadius: 12,
    fontFamily: "'Sora',sans-serif",
    fontSize: ".93rem",
    background: "#fff",
    color: "#0f172a",
    boxShadow: "0 1px 3px rgba(15,23,42,.07), 0 1px 2px rgba(15,23,42,.04)",
    transition: "all .22s ease",
    "&:hover": { background: "#fafcff" },
  },
  "& .MuiOutlinedInput-root": {
    "& fieldset": { borderColor: "#dde5f2", borderWidth: 1.5 },
    "&:hover fieldset": { borderColor: "#93c5fd" },
    "&.Mui-focused fieldset": {
      borderColor: "#3b82f6",
      borderWidth: 2,
      boxShadow: "0 0 0 5px rgba(59,130,246,.1)",
    },
  },
  "& .MuiInputLabel-root": {
    fontFamily: "'Sora',sans-serif",
    color: "#94a3b8",
    fontSize: ".88rem",
  },
  "& .MuiInputLabel-root.Mui-focused": { color: "#3b82f6" },
});

const SignInBtn = styled(Button)({
  borderRadius: 13,
  padding: "14px 24px",
  fontSize: ".96rem",
  fontWeight: 700,
  textTransform: "none",
  fontFamily: "'Sora',sans-serif",
  background:
    "linear-gradient(105deg,#1e40af 0%,#2563eb 40%,#3b82f6 70%,#60a5fa 100%)",
  backgroundSize: "200% auto",
  color: "#fff",
  boxShadow: "0 12px 32px -6px rgba(37,99,235,.44)",
  transition: "all .32s cubic-bezier(.22,1,.36,1)",
  "&:hover": {
    animation: `${shimmerBtn} 1.2s linear infinite`,
    transform: "translateY(-3px)",
    boxShadow: "0 20px 42px -8px rgba(37,99,235,.54)",
  },
  "&:active": { transform: "translateY(-1px)" },
  "&.Mui-disabled": {
    background: "#e2e8f0",
    color: "#94a3b8",
    boxShadow: "none",
  },
});


// ── component ──
export default function StudentCodeLogin() {
  const navigate = useNavigate();
  const [studCode, setStudCode] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showCode, setShowCode] = useState(false);

  useEffect(() => {
    const link = document.createElement("link");
    link.href =
      "https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700;800&family=DM+Serif+Display:ital@0;1&display=swap";
    link.rel = "stylesheet";
    document.head.appendChild(link);
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMessage("");
    try {
      const res = await fetch(`${API_URL}/login-S/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ studCode }),
      });
      const data = await res.json();
      console.log("LOGIN RESPONSE:", data);
      if (res.ok) {
        const isInstitutionStudent = data.user_type === "institution_student";
        // DYNAMIC API SWITCHING 
        const selectedAPI = isInstitutionStudent ? LOCAL_API : API_URL;
        localStorage.setItem("API_URL", selectedAPI);
        localStorage.setItem(
          "authToken",
          JSON.stringify({ access: data.access, refresh: data.refresh })
        );

        localStorage.setItem("stud_code", studCode);
        localStorage.setItem("user_type", data.user_type);
        if (data.user_type === "regular_student") {
          if (data.is_inst) navigate("/waec-subject-page");
          else if (data.country_currency?.trim().toLowerCase() === "ngn")
            navigate("/jamb-subjects");
          else navigate("/waec-subject-page");
        } else if (data.user_type === "institution_student")
          navigate("/subjects-screen");
        else navigate("/waec-subject-page");
      } else {
        if (data.detail === "Account is not active" || res.status === 403)
          setShowModal(true);
        else
          setErrorMessage(
            data.detail ||
              data.error ||
              "Incorrect student code. Please try again."
          );
      }
    } catch {
      setErrorMessage("An error occurred. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  if (!isSEB()) {
    return <h1>Please open this exam in Safe Exam Browser</h1>;
  }

  const stars = [
    { x: "11%", y: "16%", s: 4, delay: 0.2 },
    { x: "29%", y: "7%", s: 2, delay: 0.9 },
    { x: "68%", y: "13%", s: 3, delay: 1.5 },
    { x: "84%", y: "29%", s: 2, delay: 0.5 },
    { x: "91%", y: "54%", s: 4, delay: 1.9 },
    { x: "77%", y: "71%", s: 2, delay: 1.0 },
    { x: "52%", y: "84%", s: 3, delay: 1.3 },
    { x: "14%", y: "77%", s: 2, delay: 0.7 },
    { x: "4%", y: "47%", s: 3, delay: 1.7 },
    { x: "42%", y: "11%", s: 2, delay: 2.1 },
    { x: "61%", y: "91%", s: 2, delay: 0.3 },
    { x: "34%", y: "59%", s: 2, delay: 1.1 },
    { x: "56%", y: "37%", s: 2, delay: 0.6 },
  ];

  const features = [
    {
      icon: "📖",
      title: "SCHOOL CBT • WAEC • JAMB",
      sub: "write your school exam and also Practice real past questions from top exams",
      bg: "rgba(37,99,235,.12)",
      delay: ".32s",
    },
    {
      icon: "⏱️",
      title: "Timed  Exams",
      sub: "Build speed and confidence under pressure",
      bg: "rgba(16,185,129,.1)",
      delay: ".46s",
    },
    {
      icon: "🎯",
      title: "Smart Result Analysis",
      sub: "Know your strengths and focus areas instantly",
      bg: "rgba(245,158,11,.1)",
      delay: ".60s",
    },
  ];

  return (
    <Root>
      {/* ══════════ LEFT ══════════ */}
      <Left>
        <MeshBg />
        <GridPat />
        <ScanBeam />
        <Ring delay="0s" />
        <Ring delay="1.8s" />
        <Ring delay="3.6s" />

        <GlowBlob
          l="-140px"
          t="-120px"
          size="520px"
          color="radial-gradient(circle,rgba(37,99,235,.52),transparent)"
          anim={orbDrift1}
          delay={0}
        />
        <GlowBlob
          l="55%"
          t="50%"
          size="380px"
          color="radial-gradient(circle,rgba(16,185,129,.27),transparent)"
          anim={orbDrift2}
          delay={2}
        />
        <GlowBlob
          l="22%"
          t="62%"
          size="250px"
          color="radial-gradient(circle,rgba(139,92,246,.2),transparent)"
          anim={orbDrift1}
          delay={5}
        />

        {stars.map((d, i) => (
          <StarDot key={i} x={d.x} y={d.y} s={d.s} delay={d.delay} />
        ))}

        <Box sx={{ position: "relative", zIndex: 1 }}>
          {/* Brand */}
          <Box
            sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 4.5 }}
          >
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: "14px",
                background: "linear-gradient(135deg,#1e3a8a,#2563eb)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 8px 24px -6px rgba(37,99,235,.5)",
                overflow: "hidden",
              }}
            >
              <img
                src={logo}
                alt="LiteUpper"
                style={{
                  width: 40,
                  height: 40,
                  objectFit: "contain",
                  borderRadius: 10,
                }}
              />
            </Box>
            <Box>
              <Typography
                sx={{
                  fontFamily: "'Sora',sans-serif",
                  fontWeight: 800,
                  color: "#fff",
                  fontSize: "1.06rem",
                  lineHeight: 1,
                  letterSpacing: "-.01em",
                }}
              >
                LiteUpper
              </Typography>
              <Typography
                sx={{
                  fontFamily: "'Sora',sans-serif",
                  color: "rgba(255,255,255,.3)",
                  fontSize: ".63rem",
                  letterSpacing: ".12em",
                  textTransform: "uppercase",
                }}
              >
                Exam Platform
              </Typography>
            </Box>
          </Box>

          {/* Live badge */}
          <LiveBadge>
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                background: "#4ade80",
                animation: `${pulse} 2s ease-in-out infinite`,
                boxShadow: "0 0 10px rgba(74,222,128,.6)",
              }}
            />
            <Typography
              sx={{
                fontFamily: "'Sora',sans-serif",
                color: "rgba(255,255,255,.7)",
                fontSize: ".72rem",
                fontWeight: 600,
                letterSpacing: ".06em",
              }}
            >
              SECURE PORTAL · LIVE
            </Typography>
          </LiveBadge>

          {/* Hero headline */}
          <Typography
            sx={{
              fontFamily: "'DM Serif Display',serif",
              fontSize: "2.9rem",
              color: "#fff",
              lineHeight: 1.1,
              mb: 1.2,
              letterSpacing: "-.025em",
            }}
          >
            Where students
            <br />
            <Box
              component="span"
              sx={{
                fontStyle: "italic",
                background:
                  "linear-gradient(90deg,#93c5fd 0%,#60a5fa 40%,#a78bfa 80%,#f0abfc 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              prove their best.
            </Box>
          </Typography>

          {/* Animated accent line */}
          <Box
            sx={{
              height: 2,
              borderRadius: 2,
              mb: 2.5,
              background:
                "linear-gradient(90deg,#3b82f6,#818cf8 60%,transparent 100%)",
              animation: `${lineGrow} 1s cubic-bezier(.22,1,.36,1) .5s both`,
              transformOrigin: "left",
            }}
          />

          <Typography
            sx={{
              fontFamily: "'Sora',sans-serif",
              color: "rgba(255,255,255,.4)",
              fontSize: ".88rem",
              lineHeight: 1.85,
              mb: 4,
              maxWidth: 336,
            }}
          >
            Sign in with your code to access exams, and past questions
          </Typography>

          {/* Feature rows */}
          {features.map((f) => (
            <FeatureRow key={f.title} delay={f.delay}>
              <Box
                sx={{
                  width: 38,
                  height: 38,
                  borderRadius: 11,
                  background: f.bg,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "1.2rem",
                  flexShrink: 0,
                }}
              >
                {f.icon}
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography
                  sx={{
                    fontFamily: "'Sora',sans-serif",
                    fontWeight: 700,
                    color: "#e2e8f0",
                    fontSize: ".86rem",
                    mb: 0.18,
                  }}
                >
                  {f.title}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: "'Sora',sans-serif",
                    color: "rgba(255,255,255,.33)",
                    fontSize: ".77rem",
                  }}
                >
                  {f.sub}
                </Typography>
              </Box>
              <CheckCircleOutlineIcon
                sx={{ color: "rgba(255,255,255,.14)", fontSize: 17 }}
              />
            </FeatureRow>
          ))}

          {/* Divider row with dots */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              mt: 3.5,
              mb: 3,
            }}
          >
            <Box
              sx={{ height: 1, flex: 1, background: "rgba(255,255,255,.07)" }}
            />
            {[...Array(5)].map((_, i) => (
              <Box
                key={i}
                sx={{
                  width: i === 0 ? 18 : 6,
                  height: 6,
                  borderRadius: 100,
                  background:
                    i === 0
                      ? "linear-gradient(90deg,#3b82f6,#818cf8)"
                      : "rgba(255,255,255,.1)",
                }}
              />
            ))}
          </Box>

          {/* Stats row */}
          <Box sx={{ display: "flex", gap: 4 }}>
            {[
              { val: "80+", label: "Past Questions" },
              { val: "100+", label: " Exams Taken" },
              { val: "85%", label: "Avg. Score Improvement" },
            ].map((s) => (
              <Box key={s.label}>
                <Typography
                  sx={{
                    fontFamily: "'DM Serif Display',serif",
                    color: "#c7d2e0",
                    fontSize: "1.08rem",
                    lineHeight: 1,
                  }}
                >
                  {s.val}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: "'Sora',sans-serif",
                    color: "rgba(255,255,255,.25)",
                    fontSize: ".62rem",
                    letterSpacing: ".07em",
                    textTransform: "uppercase",
                    mt: 0.3,
                  }}
                >
                  {s.label}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Left>

      {/* ══════════ RIGHT ══════════ */}
      <Right>
        <NoiseTex />
        <CornerBlob pos="tr" />
        <CornerBlob pos="bl" />

        {/* Tiny decorative rings top-right */}
        <Box
          sx={{
            position: "absolute",
            top: 38,
            right: 38,
            width: 54,
            height: 54,
            borderRadius: "50%",
            border: "1px solid rgba(59,130,246,.11)",
            pointerEvents: "none",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            top: 48,
            right: 48,
            width: 34,
            height: 34,
            borderRadius: "50%",
            border: "1px solid rgba(59,130,246,.08)",
            pointerEvents: "none",
          }}
        />

        <GlassCard>
          {/* Logo mark */}
          <LogoWrap>
            <LogoBox>
              <LockOutlinedIcon sx={{ fontSize: 28, color: "#fff" }} />
            </LogoBox>
          </LogoWrap>

          {/* Heading */}
          <Typography
            sx={{
              fontFamily: "'DM Serif Display',serif",
              fontSize: "2.05rem",
              color: "#0a1628",
              lineHeight: 1.16,
              mb: 0.65,
              letterSpacing: "-.018em",
            }}
          >
            Welcome back
          </Typography>
          <Typography
            sx={{
              fontFamily: "'Sora',sans-serif",
              color: "#8394a7",
              fontSize: ".85rem",
              mb: 3.5,
              lineHeight: 1.62,
            }}
          >
            Enter your student code to access your portal
          </Typography>

          <form onSubmit={handleLogin}>
            {/* Field label row */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 0.85,
              }}
            >
              <Typography
                sx={{
                  fontFamily: "'Sora',sans-serif",
                  fontSize: ".7rem",
                  fontWeight: 700,
                  color: "#4b5f75",
                  letterSpacing: ".09em",
                  textTransform: "uppercase",
                }}
              >
                Student Code
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    background: "#22c55e",
                    boxShadow: "0 0 7px rgba(34,197,94,.7)",
                  }}
                />
                <Typography
                  sx={{
                    fontFamily: "'Sora',sans-serif",
                    fontSize: ".63rem",
                    color: "#94a3b8",
                    fontWeight: 500,
                  }}
                >
                  Encrypted
                </Typography>
              </Box>
            </Box>

            <StyledInput
              fullWidth
              variant="outlined"
              value={studCode}
              onChange={(e) => setStudCode(e.target.value)}
              required
              placeholder="Enter your student code"
              autoFocus
              type={showCode ? "text" : "password"}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowCode(!showCode)}
                      edge="end"
                      size="small"
                      sx={{
                        color: "#94a3b8",
                        "&:hover": {
                          color: "#3b82f6",
                          background: "rgba(59,130,246,.07)",
                        },
                        borderRadius: "8px",
                        mr: 0.2,
                        transition: "all .2s",
                      }}
                    >
                      {showCode ? (
                        <VisibilityOff sx={{ fontSize: 18 }} />
                      ) : (
                        <Visibility sx={{ fontSize: 18 }} />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {errorMessage && (
              <Alert
                severity="error"
                sx={{
                  mt: 2.2,
                  mb: 0,
                  borderRadius: "11px",
                  fontFamily: "'Sora',sans-serif",
                  fontSize: ".82rem",
                  background: "#fff1f2",
                  border: "1.5px solid #fecdd3",
                  color: "#be123c",
                  "& .MuiAlert-icon": { color: "#f43f5e" },
                  animation: `${riseIn} .3s ease both`,
                }}
              >
                {errorMessage}
              </Alert>
            )}

            <SignInBtn
              type="submit"
              fullWidth
              disabled={loading || !studCode.trim()}
              sx={{ mt: 2.8 }}
              endIcon={
                !loading && (
                  <ArrowForwardIcon sx={{ fontSize: "1rem !important" }} />
                )
              }
            >
              {loading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                "Sign In"
              )}
            </SignInBtn>
          </form>

          {/* Divider */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 10, my: 3 }}>
            <Box
              sx={{
                flex: 1,
                height: 1,
                background: "linear-gradient(90deg,transparent,#d8e2f0)",
              }}
            />
            <Typography
              sx={{
                fontFamily: "'Sora',sans-serif",
                fontSize: ".7rem",
                color: "#b8c6d6",
                whiteSpace: "nowrap",
              }}
            >
              Need help?
            </Typography>
            <Box
              sx={{
                flex: 1,
                height: 1,
                background: "linear-gradient(90deg,#d8e2f0,transparent)",
              }}
            />
          </Box>

          {/* Footer */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1.2,
              mt: 3.5,
            }}
          >
            {["🔒 AES-256", "LiteUpper © 2026", "All Rights Reserved"].map(
              (t, i) => (
                <React.Fragment key={t}>
                  {i > 0 && (
                    <Box
                      sx={{
                        width: 3,
                        height: 3,
                        borderRadius: "50%",
                        background: "#c9d5e0",
                      }}
                    />
                  )}
                  <Typography
                    sx={{
                      fontFamily: "'Sora',sans-serif",
                      fontSize: ".67rem",
                      color: "#b8c6d6",
                    }}
                  >
                    {t}
                  </Typography>
                </React.Fragment>
              )
            )}
          </Box>
        </GlassCard>
      </Right>

      {/* ══════════ INACTIVE MODAL ══════════ */}
      <Modal
        open={showModal}
        onClose={() => setShowModal(false)}
        closeAfterTransition
      >
        <Fade in={showModal}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%,-50%)",
              width: 372,
              bgcolor: "#fff",
              borderRadius: "24px",
              boxShadow:
                "0 56px 100px -24px rgba(15,23,42,.28), 0 0 0 1px rgba(15,23,42,.06)",
              p: "46px 40px",
              textAlign: "center",
              outline: "none",
              animation: `${riseIn} .35s cubic-bezier(.22,1,.36,1) both`,
            }}
          >
            <Box
              sx={{
                width: 72,
                height: 72,
                borderRadius: "22px",
                background: "linear-gradient(135deg,#fef2f2,#fee2e2)",
                border: "2px solid #fecdd3",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 22px",
                fontSize: "2rem",
                boxShadow: "0 12px 28px -8px rgba(239,68,68,.2)",
              }}
            >
              🔒
            </Box>
            <Typography
              sx={{
                fontFamily: "'DM Serif Display',serif",
                fontSize: "1.5rem",
                color: "#0a1628",
                mb: 1.2,
                letterSpacing: "-.01em",
              }}
            >
              No Active Plan
            </Typography>
            <Typography
              sx={{
                fontFamily: "'Sora',sans-serif",
                color: "#64748b",
                fontSize: ".87rem",
                lineHeight: 1.7,
                mb: 3.5,
              }}
            >
              Your account is currently inactive. Please reach out to your
              institution administrator to activate your plan.
            </Typography>
            <Button
              onClick={() => setShowModal(false)}
              fullWidth
              variant="contained"
              sx={{
                py: 1.55,
                borderRadius: "13px",
                textTransform: "none",
                fontFamily: "'Sora',sans-serif",
                fontWeight: 700,
                fontSize: ".93rem",
                background: "linear-gradient(105deg,#1e40af,#2563eb,#3b82f6)",
                backgroundSize: "200% auto",
                boxShadow: "0 10px 24px -6px rgba(37,99,235,.42)",
                "&:hover": {
                  backgroundPosition: "right center",
                  transform: "translateY(-2px)",
                  boxShadow: "0 16px 32px -8px rgba(37,99,235,.52)",
                },
                transition: "all .28s ease",
              }}
            >
              Got it, thanks
            </Button>
          </Box>
        </Fade>
      </Modal>
    </Root>
  );
}
